import {WebsiteCaptureDialog} from '@/components/brands/website-capture-dialog';
import {captureAvailable, captureWebsite, saveCaptureBrand} from '@/components/brands/capture-client';
import {visibleOnboardingPhases} from '@/components/brands/onboarding-presentation';
import {resolveOffering} from '@shared/offering';
import {SoftwareOfferingSetup} from '@/components/brands/software-offering-setup';
// client/src/pages/onboarding-wizard.tsx
// v7.07 — FIRST-RUN ONBOARDING WIZARD (Dan's spec).
//
// Full-screen, post-checkout, three steps, skippable at any point:
//   1. Add your brand   — URL → scrape → create brand → the existing 8-phase
//                          scan starts and its progress bar runs ON SCREEN.
//   2. Create project   — NO user input. Auto-named after the brand, with the
//                          brand assigned. Runs while step 1's scan continues
//                          in the background.
//   3. Add product      — product URL → scraped name + image, shown, saved.
//
// It composes EXISTING pipelines end to end — nothing here is a new backend
// capability. The only new server state is users.onboarding_completed_at,
// which is stamped on BOTH finish and skip so the wizard never nags twice.
//
// STYLING: consumes auth-theme.ts — the SAME single source of truth the four
// auth pages use. The wizard is the next beat in that exact flow (signup →
// plan → wizard), and that module exists precisely because these surfaces
// "drifted three different ways before"; hardcoding a fourth palette here
// would re-open the bug it was written to close.
import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import {ProductVariantPicker} from "@/components/brands/ProductVariantPicker";
import type {DetectedProductVariant} from "@shared/productVariants";
import LogoAnimation from "@/components/LogoAnimation";
import {
  PAGE, CARD as CARD_BG, INK, MUTED, LINE, FIELD, PINK,
  SANS, SERIF, pageStyle, cardStyle, monoLabelStyle, h1Style, serifStyle,
  subStyle, labelStyle, fieldStyle, buttonStyle, errorStyle, footerStyle,
  fieldFocus, fieldBlur, btnHoverOn, btnHoverOff,
} from "./auth-theme";

const PAGE_BG = PAGE;
const CARD: React.CSSProperties = cardStyle;
const FAINT = MUTED;
const ACCENT = PINK;

const STEPS = ["Your brand", "Your workspace", "Your product"] as const;

// ── Small shared controls (copied from the auth pages' idiom, incl. the
//    imperative focus ring signup.tsx uses — Tailwind focus: classes would
//    look different from the page the user just came from) ─────────────────
function TextField(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onEnter?: () => void;
  disabled?: boolean;
  testId: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      type="text"
      value={props.value}
      disabled={props.disabled}
      autoFocus={props.autoFocus}
      data-testid={props.testId}
      onChange={(e) => props.onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && props.onEnter) props.onEnter();
      }}
      placeholder={props.placeholder}
      className="w-full h-11 px-3 outline-none transition-all disabled:opacity-60"
      style={fieldStyle}
      onFocus={fieldFocus}
      onBlur={fieldBlur}
    />
  );
}

function PrimaryButton(props: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <button
      onClick={props.onClick}
      disabled={props.disabled || props.loading}
      data-testid={props.testId}
      className="w-full transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
      style={buttonStyle}
      onMouseEnter={(e) => btnHoverOn(e, props.loading)}
      onMouseLeave={btnHoverOff}
    >
      {props.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {props.children}
    </button>
  );
}

function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-sm px-4 py-3"
      style={errorStyle}
      data-testid="onboarding-error"
    >
      {children}
    </div>
  );
}

// ── Brand-scan progress ─────────────────────────────────────────────────────
// The 8/9-phase job (brand_onboarding_jobs). Landmines honored, all three
// documented in canvas-brands-view.tsx's OnboardingScanCard:
//   (1) that card RETURNS NULL on 'completed' — useless for a wizard that must
//       show success, so this renders its own terminal state;
//   (2) 'completed_with_errors' is TERMINAL, not running — treating it as
//       in-progress spins forever;
//   (3) phases[] is empty for the first beat (startOnboarding is
//       fire-and-forget, so the job row may not exist yet) — that reads as
//       "Starting…", never as failure.
type PhaseStatus = "pending" | "running" | "completed" | "failed" | "skipped";
interface OnboardingPhase { id: string; name?: string; status: PhaseStatus }
interface OnboardingJob {
  status: "pending" | "running" | "completed" | "completed_with_errors" | "failed";
  phases?: OnboardingPhase[];
  error_message?: string | null;
}

const PHASE_TEXT: Record<string, string> = {
  brand_dna: "Reading your brand's DNA",
  competitor_discovery: "Finding your competitors",
  competitor_research: "Researching competitors",
  brand_ads: "Pulling your live ads",
  competitor_ads: "Pulling competitor ads",
  visual_identity: "Learning your visual identity",
  competitor_insights: "Extracting competitor insights",
  generate_ad_angles: "Drafting ad angles",
};

const TERMINAL = new Set(["completed", "completed_with_errors", "failed"]);

function ScanProgress({ job }: { job: OnboardingJob | null }) {
  const phases = visibleOnboardingPhases(job?.phases);
  const done = phases.filter((p) => p.status === "completed" || p.status === "skipped").length;
  // Total falls back to the real pipeline length (8) rather than the stale 6
  // the old card used, so early percentages don't overstate progress.
  const total = phases.length || 8;
  const finished = job ? TERMINAL.has(job.status) : false;
  const pct = finished ? 100 : Math.round((done / total) * 100);
  const running = phases.find((p) => p.status === "running");
  const caption = !job
    ? "Starting the scan…"
    : finished
      ? "Brand intelligence ready"
      : running
        ? PHASE_TEXT[running.id] ?? running.name ?? "Working…"
        : "Starting the scan…";

  return (
    <div
      className="rounded-xl px-4 py-3.5"
      style={{ border: `1px solid ${LINE}`, backgroundColor: FIELD, borderRadius: 11 }}
      data-testid="onboarding-scan-progress"
    >
      <div className="flex items-center gap-2.5">
        {finished ? (
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: "#16a34a" }} />
        ) : (
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" style={{ color: ACCENT }} />
        )}
        <span className="text-sm font-medium" style={{ color: INK }}>{caption}</span>
        <span className="text-xs ml-auto tabular-nums" style={{ color: MUTED }}>
          {finished ? `${total}/${total}` : `${done}/${total}`}
        </span>
      </div>
      <div
        style={{ width: "100%", height: 5, background: LINE, borderRadius: 3, overflow: "hidden", marginTop: 10 }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.max(pct, 3)}%`,
            background: finished ? "#16a34a" : ACCENT,
            borderRadius: 3,
            transition: "width 0.7s ease-out",
          }}
        />
      </div>
      <p className="text-xs mt-2.5" style={{ color: FAINT }}>
        {finished
          ? "You can keep going — everything below is already saved."
          : "This keeps running in the background. You don't have to wait for it."}
      </p>
    </div>
  );
}

// ── Step rail ───────────────────────────────────────────────────────────────
function StepRail({ current, software=false }: { current: number; software?:boolean }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-7" data-testid="onboarding-steps">
      {STEPS.map((originalLabel, i) => {
        const label=software&&i===2?"Your platform":originalLabel;
        const state = i < current ? "done" : i === current ? "active" : "todo";
        return (
          <div key={label} className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <div
                className="flex items-center justify-center rounded-full text-[11px] font-semibold"
                style={{
                  width: 20,
                  height: 20,
                  backgroundColor: state === "todo" ? LINE : state === "active" ? INK : "#16a34a",
                  color: state === "todo" ? FAINT : "#FFFFFF",
                }}
              >
                {state === "done" ? "✓" : i + 1}
              </div>
              <span
                className="text-xs font-medium hidden sm:inline"
                style={{ color: state === "todo" ? FAINT : INK }}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ width: 22, height: 1, backgroundColor: LINE }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── The wizard ──────────────────────────────────────────────────────────────
export default function OnboardingWizard({ onDone }: { onDone: () => Promise<void> | void }) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  // Step 1
  const [captureUrl, setCaptureUrl] = useState<string | null>(null);
  const [brandUrl, setBrandUrl] = useState("");
  const [brandBusy, setBrandBusy] = useState(false);
  const [brand, setBrand] = useState<Record<string,any> | null>(null);
  const software=resolveOffering(brand).type==='software';
  const [job, setJob] = useState<OnboardingJob | null>(null);

  // Step 2
  const [project, setProject] = useState<{ id: number; name: string } | null>(null);
  const [projectBusy, setProjectBusy] = useState(false);
  const projectStartedRef = useRef(false);

  // Step 3
  const [productUrl, setProductUrl] = useState("");
  const [productBusy, setProductBusy] = useState(false);
  const [productSaved, setProductSaved] = useState(false);
  const [productPhoto, setProductPhoto] = useState<string | null>(null);
  const [productName, setProductName] = useState("");
  const includeVariants = false; // First-time setup imports one product; variants remain available in Brands.
  const [variants, setVariants] = useState<DetectedProductVariant[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<Set<string>>(new Set());
  const [productScan, setProductScan] = useState<Record<string, any> | null>(null);
  const productOperation = useRef(false);
  const productEpoch = useRef(0);
  const productController = useRef<AbortController | null>(null);
  const finishStarted = useRef(false);
  const reviewingVariants = includeVariants && productScan !== null;
  const importingVariants = reviewingVariants && variants.length > 0;
  useEffect(() => () => { productEpoch.current++; productController.current?.abort(); }, []);
  const clearProductScan = () => {
    productEpoch.current++;
    productController.current?.abort();
    setProductScan(null); setVariants([]); setSelectedVariants(new Set());
    setProductName(""); setProductPhoto(null); setError(null);
  };

  // Poll the brand scan for as long as the wizard is open. It deliberately
  // outlives step 1 — the scan keeps running while the user does steps 2-3,
  // which is the whole point of ordering the steps this way.
  useEffect(() => {
    if (!brand) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const res = await fetch(`/api/brands/${brand.id}/onboarding`);
        if (alive && res.ok) {
          const data = (await res.json()) as OnboardingJob | null;
          if (data) setJob(data);
          if (data && TERMINAL.has(data.status)) return; // stop polling
        }
      } catch {
        /* transient — keep polling */
      }
      if (alive) timer = setTimeout(tick, 3000);
    };
    tick();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [brand]);

  const finish = useCallback(
    async (skipped: boolean) => {
      if (finishStarted.current || (skipped && productOperation.current)) return;
      finishStarted.current = true;
      setFinishing(true);
      try {
        await fetch("/api/onboarding/complete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ skipped }),
        });
      } catch {
        /* never trap the user in the wizard because this write failed */
      }
      try {
        await onDone();
      } catch (error) {
        finishStarted.current = false;
        setFinishing(false);
        throw error;
      }
    },
    [onDone],
  );

  // ── Step 1: scrape + create brand, then move on immediately ──────────────
  const submitBrand = async () => {
    const url = brandUrl.trim();
    if (!url) return;
    setError(null);
    setBrandBusy(true);
    try {
      const genRes = await fetch("/api/brand/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const gen = await genRes.json();
      if (!genRes.ok || !gen?.success) {
        if (await captureAvailable(gen)) { setCaptureUrl(captureWebsite(url)); return; }
        throw new Error(gen?.message || "Couldn't read that site. Check the URL and try again.");
      }
      const d = gen.data;
      // Same snake→camel mapping AnalyzeBrandModal uses, so a wizard-created
      // brand is byte-identical to a modal-created one.
      const payload = {
        name: d.brand_name || new URL(url.startsWith("http") ? url : `https://${url}`).hostname,
        industry: d.industry_vertical || "",
        website: d.website || url,
        description: d.product_service_description || "",
        targetAudience: d.target_audience || "",
        toneOfVoice: d.tone_of_voice || "",
        logo: d.logo_url || null,
        colors: d.brand_colors || [],
        painPoints: d.pain_points || [],
        purchaseTriggers: d.purchase_triggers || [],
        customerObjections: d.customer_objections || [],
        desiredTransformation: d.desired_transformation || null,
        customerAvatarName: d.customer_avatar_name || null,
        awarenessLevel: d.awareness_level || null,
        brandPersonality: d.brand_personality || [],
        contentPillars: d.content_pillars || [],
        voiceWordsUsed: d.voice_words_used || [],
        voiceWordsAvoided: d.voice_words_avoided || [],
        additionalInfo: d.additional_information || "",
        competitorPositioning: d.competitor_positioning || null,
        keyStats: d.key_stats || [],
        heroTestimonials: d.hero_testimonials || [],
        pressMentions: d.press_mentions || [],
        beforeAfterResults: d.before_after_results || [],
        priceAnchoring: d.price_anchoring || null,
        hasSubscription: d.has_subscription || false,
        heroSku: d.hero_sku || null,
        offerPatterns: d.offer_patterns || [],
        photographyStyle: d.photography_style || [],
        brandEnvironments: d.brand_environments || [],
        modelDirection: d.model_direction || null,
        ageRange: d.age_range || null,
        genderSkew: d.gender_skew || null,
        geographyFocus: d.geography_focus || [],
        platformPrimary: d.platform_primary || [],
        audienceInterests: d.audience_interests || [],
        funnelStage: d.funnel_stage || null,
        funnelGoal: d.funnel_goal || null,
        headlineFont: d.fonts?.headline?.family || null,
        headlineFontWeight: d.fonts?.headline?.weight || null,
        bodyFont: d.fonts?.body?.family || null,
        bodyFontWeight: d.fonts?.body?.weight || null,
      };
      const saveRes = await fetch("/api/brands", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const saved = await saveRes.json();
      if (!saveRes.ok) {
        if (saveRes.status === 402) throw new Error("You're out of credits — top up to finish setting up your brand.");
        throw new Error(saved?.message || "Couldn't save the brand.");
      }
      setBrand(saved);
      // v7.08 — POST /api/brands now creates the brand's project FOLDER
      // server-side (every brand gets one, from any surface). Step 2 shows
      // that folder rather than creating a second one; it only creates a
      // folder itself if the server's fail-open path returned none.
      if (typeof saved.folderId === "number") {
        setProject({ id: saved.folderId, name: saved.name });
        projectStartedRef.current = true;
      }
      setStep(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBrandBusy(false);
    }
  };

  // ── Step 2: auto-create the project. No input — fires on entry. ──────────
  // v7.08 — FALLBACK ONLY. The folder is normally created server-side by
  // POST /api/brands and arrives as saved.folderId (which pre-sets the ref),
  // so this runs solely when that fail-open path returned nothing.
  //
  // ONE-SHOT via a ref, deliberately: with `projectBusy` in the dep array the
  // effect re-ran the instant setProjectBusy(true) landed, and the FIRST
  // run's cleanup flipped its own `alive` flag to false — so the in-flight
  // POST resolved into a dead closure and the card span "Creating your
  // workspace…" forever (caught live in the harness). The ref makes entry
  // idempotent without needing the busy flag as a dependency.
  useEffect(() => {
    if (step !== 1 || !brand || projectStartedRef.current) return;
    projectStartedRef.current = true;
    (async () => {
      setProjectBusy(true);
      setError(null);
      try {
        const res = await fetch("/api/canvas/projects", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: brand.name, brandId: brand.id, isFolder: true }),
        });
        const created = await res.json();
        if (!res.ok) throw new Error(created?.error || "Couldn't create your workspace.");
        setProject({ id: created.id, name: created.name });
      } catch (e) {
        // Re-arm so Continue can retry rather than stranding the user.
        projectStartedRef.current = false;
        setError(e instanceof Error ? e.message : "Couldn't create your workspace.");
      } finally {
        setProjectBusy(false);
      }
    })();
  }, [step, brand]);

  // The ordinary path still analyzes and saves one product. Variant scanning
  // stops for explicit review, then uses the same idempotent import as Brands.
  const scrapeProduct = async () => {
    const url = productUrl.trim();
    if (!url || !brand || productOperation.current || productSaved || finishStarted.current) return;
    if (importingVariants && selectedVariants.size === 0) return;
    productOperation.current = true;
    const epoch = ++productEpoch.current;
    setError(null);
    setProductBusy(true);
    try {
      let data = productScan;
      if (!data) {
        const controller = new AbortController();
        productController.current = controller;
        const timeout = setTimeout(() => controller.abort(), 35_000);
        try {
          const response = await fetch("/api/product/analyze", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url, includeVariants }), signal: controller.signal,
          });
          const result = await response.json();
          if (epoch !== productEpoch.current) return;
          if (!response.ok || !result?.success) throw new Error(result?.message || "Couldn't read that product page. Check the link and try again.");
          data = result.data;
          if (!data || typeof data !== "object") throw new Error("Couldn't read that product page. Please try again.");
        } finally {
          clearTimeout(timeout);
          productController.current = null;
        }
        setProductScan(data);
        setProductName(data.product_name || "");
        setProductPhoto(Array.isArray(data.photos) ? data.photos[0] ?? null : null);
        if (includeVariants) {
          const detected: DetectedProductVariant[] = Array.isArray(data.variants) ? data.variants : [];
          setVariants(detected);
          setSelectedVariants(new Set(detected.filter(variant => variant.imageUrl).map(variant => variant.source.variantKey)));
          return; // Never import variants or finish before selection review.
        }
      }

      if (importingVariants) {
        const selected = variants.filter(variant => variant.imageUrl && selectedVariants.has(variant.source.variantKey));
        if (!selected.length) return;
        const response = await fetch(`/api/brands/${brand.id}/products/import-variants`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ variants: selected }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result?.message || "Couldn't import the variants. Your selection is kept — try again.");
        if (!Array.isArray(result.products) || result.products.length !== selected.length) throw new Error("Could not confirm all selected variants were imported. Retry safely to check them again.");
        setProductName(`${selected.length} variant${selected.length === 1 ? "" : "s"}`);
        setProductPhoto(selected[0].imageUrl);
      } else {
        const response = await fetch(`/api/brands/${brand.id}/products`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: data.product_name || "My product", description: data.description || "",
            uniqueFeatures: data.unique_features || "", taglines: data.taglines || [],
            photos: data.photos || [], productUrl: data.product_url || url,
          }),
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result?.message || "Couldn't save the product.");
        }
      }
      if (epoch !== productEpoch.current) return;
      setProductSaved(true);
      // Keep both Finish and Skip locked until the import and completion settle.
      await new Promise(resolve => setTimeout(resolve, 1400));
      if (epoch === productEpoch.current) await finish(false);
    } catch (error) {
      if (epoch === productEpoch.current) setError(error instanceof Error && error.name === "AbortError"
        ? "Reading the product timed out. Please try again."
        : error instanceof Error ? error.message : "Something went wrong.");
    } finally {
      if (epoch === productEpoch.current) { productOperation.current = false; setProductBusy(false); }
    }
  };

  if (captureUrl) return <WebsiteCaptureDialog website={captureUrl} onCancel={()=>setCaptureUrl(null)} onSave={async(result, captureRequestId, edits)=>{
    const saved = await saveCaptureBrand(result, captureRequestId, edits);
    setBrand(saved);
    if (typeof saved.folderId === 'number') {setProject({id:saved.folderId,name:saved.name});projectStartedRef.current=true;}
    setCaptureUrl(null); setStep(1);
  }}/>;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={pageStyle}
      data-testid="onboarding-wizard"
    >
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <LogoAnimation size={120} />
        </div>

        <StepRail current={step} software={software} />

        <div style={CARD}>
          {/* ── Step 1 ── */}
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <h1 style={{ ...h1Style, fontSize: 26 }}>
                  What brand are we working on?
                </h1>
                <p style={subStyle}>
                  Drop your website in and we'll study your brand — your voice, colors, products,
                  and the ads your competitors are running.
                </p>
              </div>
              <div>
                <label className="block mb-1.5" style={labelStyle}>
                  Website
                </label>
                <TextField
                  value={brandUrl}
                  onChange={setBrandUrl}
                  onEnter={submitBrand}
                  placeholder="yourbrand.com"
                  disabled={brandBusy}
                  testId="onboarding-brand-url"
                  autoFocus
                />
              </div>
              {error && <ErrorNote>{error}</ErrorNote>}
              <PrimaryButton
                onClick={submitBrand}
                disabled={!brandUrl.trim()}
                loading={brandBusy}
                testId="onboarding-brand-submit"
              >
                {brandBusy ? "Reading your site…" : "Continue"}
                {!brandBusy && <ArrowRight className="h-4 w-4" />}
              </PrimaryButton>
              {brandBusy && (
                <p className="text-xs text-center" style={{ color: FAINT }}>
                  This takes 15–30 seconds.
                </p>
              )}
            </div>
          )}

          {/* ── Step 2 ── */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h1 style={{ ...h1Style, fontSize: 26 }}>
                  {brand?.name} is set up
                </h1>
                <p style={subStyle}>
                  We made you a project folder for it, ready to go. Your brand scan is running
                  now — it keeps going while you finish setting up.
                </p>
              </div>

              <ScanProgress job={job} />

              <div
                className="rounded-xl px-4 py-3.5 flex items-center gap-2.5"
                style={{ border: `1px solid ${LINE}`, backgroundColor: FIELD, borderRadius: 11 }}
                data-testid="onboarding-project-state"
              >
                {projectBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" style={{ color: ACCENT }} />
                ) : (
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: "#16a34a" }} />
                )}
                <span className="text-sm" style={{ color: INK }}>
                  {projectBusy ? "Creating your workspace…" : `Brand folder “${project?.name ?? brand?.name}” ready`}
                </span>
              </div>

              {error && <ErrorNote>{error}</ErrorNote>}

              <PrimaryButton
                onClick={() => {
                  setError(null);
                  setStep(2);
                }}
                disabled={projectBusy}
                testId="onboarding-project-continue"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </PrimaryButton>
            </div>
          )}

          {/* ── Step 3 ── */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h1 style={{ ...h1Style, fontSize: 26 }}>
                  {software?"Add your platform":"Add your first product"}
                </h1>
                <p style={subStyle}>
                  {software?"Show the real interface your customers use.":"Paste a product page and we'll pull the image and details. This is what your ads get built around."}
                </p>
              </div>

              {brand && <ScanProgress job={job} />}

              {software&&brand?<SoftwareOfferingSetup brand={brand} onBusy={busy=>{productOperation.current=busy;setProductBusy(busy);}} onSaved={async()=>{setProductSaved(true);await finish(false);}}/>:<>
              <div>
                <label className="block mb-1.5" style={labelStyle}>
                  Product page
                </label>
                <TextField
                  value={productUrl}
                  onChange={url => { clearProductScan(); setProductUrl(url); }}
                  onEnter={scrapeProduct}
                  placeholder="yourbrand.com/products/…"
                  disabled={productBusy || productSaved || finishing}
                  testId="onboarding-product-url"
                  autoFocus
                />
              </div>

              {importingVariants && !productSaved && <ProductVariantPicker variants={variants} selected={selectedVariants} onChange={setSelectedVariants} disabled={productBusy || finishing} />}
              {reviewingVariants && !variants.length && <p className="text-sm" style={{color: MUTED}}>No separate variants found. Finish setup to save the product shown below.</p>}

              {/* What we found — shown BEFORE the wizard closes so the image
                  the ads get built around is actually seen. */}
              {productPhoto && (!importingVariants || productSaved) && (
                <div
                  className="flex items-center gap-3 px-3 py-3"
                  style={{ border: `1px solid ${LINE}`, backgroundColor: FIELD, borderRadius: 11 }}
                  data-testid="onboarding-product-preview"
                >
                  {/* A scraped photo URL can 404 or be hotlink-blocked; a
                      broken-image icon in the middle of setup looks broken.
                      Drop to a neutral tile and keep the row intact. */}
                  <img
                    src={productPhoto}
                    alt=""
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                    style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8, border: `1px solid ${LINE}`, backgroundColor: CARD_BG, flexShrink: 0 }}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: INK }}>
                      {productName || "Your product"}
                    </div>
                    <div className="text-xs" style={{ color: MUTED }}>
                      {productSaved ? "Saved to your brand" : "Found on that page"}
                    </div>
                  </div>
                  {productSaved && (
                    <CheckCircle2 className="h-4 w-4 ml-auto flex-shrink-0" style={{ color: "#16a34a" }} />
                  )}
                </div>
              )}

              {error && <ErrorNote>{error}</ErrorNote>}

              <PrimaryButton
                onClick={scrapeProduct}
                disabled={!productUrl.trim() || productSaved || finishing || (importingVariants && selectedVariants.size === 0)}
                loading={productBusy}
                testId="onboarding-product-submit"
              >
                {productSaved ? "All set" : productBusy ? (reviewingVariants ? "Saving products…" : "Reading the page…") : importingVariants ? `Import ${selectedVariants.size} variant${selectedVariants.size === 1 ? "" : "s"} & finish` : includeVariants && !reviewingVariants ? "Find variants" : "Finish setup"}
                {!productBusy && !productSaved && <ArrowRight className="h-4 w-4" />}
              </PrimaryButton>
              </>}
            </div>
          )}
        </div>

        {/* Skip — present on every step. Drops the user straight into the app
            with whatever has already been created (a brand made in step 1 is
            kept; the scan keeps running server-side). */}
        <div className="text-center mt-8" style={footerStyle}>
          <button
            onClick={() => void finish(true)}
            disabled={finishing || productBusy}
            className="hover:underline disabled:opacity-60"
            data-testid="onboarding-skip"
          >
            {finishing ? "One moment…" : step === 0 ? "Skip for now" : "Skip the rest"}
          </button>
        </div>
      </div>
    </div>
  );
}
